import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Television, Copy, ArrowSquareOut, Monitor, Info, ShieldCheck,
} from "@phosphor-icons/react";

function DisplayRow({ title, sub, url, testid, accent = "#007AFF" }) {
  const copy = () => {
    navigator.clipboard.writeText(url)
      .then(() => toast.success("TV link copied — paste it into the TV browser"))
      .catch(() => toast.error("Couldn't copy — long-press the link to copy"));
  };
  return (
    <div data-testid={testid} className="bg-[#121214] border border-white/10 p-3 flex items-center gap-3">
      <div className="w-10 h-10 shrink-0 flex items-center justify-center border" style={{ borderColor: accent + "66", background: accent + "1A" }}>
        <Monitor size={20} weight="bold" style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold tracking-tight text-sm truncate">{title}</p>
        <p className="font-mono-data text-[11px] text-[#64748B] truncate">{url}</p>
        {sub && <p className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</p>}
      </div>
      <button data-testid={`${testid}-copy`} onClick={copy} title="Copy link"
        className="w-10 h-10 flex items-center justify-center bg-[#1A1A1E] border border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30 transition-colors">
        <Copy size={17} weight="bold" />
      </button>
      <a data-testid={`${testid}-open`} href={url} target="_blank" rel="noreferrer" title="Open display"
        className="w-10 h-10 flex items-center justify-center bg-[#007AFF] text-white hover:bg-[#3395FF] transition-colors">
        <ArrowSquareOut size={17} weight="bold" />
      </a>
    </div>
  );
}

export default function TvDisplays() {
  const [cams, setCams] = useState([]);
  const origin = window.location.origin;

  useEffect(() => {
    api.get("/cameras").then((r) => setCams((r.data || []).filter((c) => c.status === "online"))).catch(() => {});
  }, []);

  return (
    <div data-testid="tv-displays-page" className="space-y-4 max-w-3xl">
      {/* Intro */}
      <div className="bg-[#121214] border border-white/10 p-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <Television size={18} weight="bold" className="text-[#BF5AF2]" /> TV Displays
        </h2>
        <p className="text-xs text-[#A1A1AA] leading-relaxed mt-1">
          Run any number of office TVs — each on its own link. <span className="text-white">TV 1</span> can show the full camera <span className="text-white">grid</span>; <span className="text-white">TV 2</span> can <span className="text-white">auto-rotate</span> through cameras (~20s each), skip quiet ones, and lock onto any camera with high activity. You can also pin a TV to a <span className="text-white">single camera</span>. The laptop stays your control center (employees, setup, alerts); TVs are display-only.
        </p>
      </div>

      {/* Setup steps */}
      <div className="bg-[#0A0A0C] border border-white/10 p-4">
        <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#32D74B] mb-2 flex items-center gap-2">
          <Info size={13} weight="bold" /> How to set up a TV (stick / box)
        </p>
        <ol className="text-xs text-[#A1A1AA] leading-relaxed list-decimal pl-4 space-y-1">
          <li>Copy the link for the display you want (button on the right).</li>
          <li>On the Fire Stick / Chromecast / mini-PC browser, open that link and log in with the <span className="text-[#FF9F0A]">store PIN</span> (each TV shows only that store's cameras).</li>
          <li>Tap <span className="text-white">Fullscreen</span> — it stays live, self-trains, and keeps the screen awake. Repeat on each TV.</li>
          <li className="text-[#64748B]">Tip: the free "Fully Kiosk Browser" app auto-opens the link fullscreen on boot for 24/7 use.</li>
        </ol>
      </div>

      {/* Hot Zone TV board */}
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#FF9F0A] mb-2">★ Hot Zone TV · auto-flipping hot-zone board</p>
        <DisplayRow
          testid="tv-display-hotzone"
          title="Hot Zone TV — 6 cameras per slide, auto-flip"
          sub="Pick any cameras in the board itself; flips through slides of 6 with live heat + people overlay. Opens with the TV code (default TV7869) — no PIN needed."
          url={`${origin}/hotzone-tv`}
          accent="#FF9F0A"
        />
      </div>

      {/* Two-screen split: Live wall + Analysis wall */}
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#32D74B] mb-2">Screen A · All Live Feeds</p>
          <DisplayRow
            testid="tv-display-live"
            title="Live Wall — smooth video only"
            sub="Every camera, clean live video, no AI overlays — the 'eyes' TV"
            url={`${origin}/live`}
            accent="#32D74B"
          />
        </div>
        <div>
          <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#FF9F0A] mb-2">Screen B · All Analysis</p>
          <DisplayRow
            testid="tv-display-analysis"
            title="Analysis Wall — AI verdicts & alerts"
            sub="Every camera with theft/vandalism scores, flags & alerts — the 'brain' TV"
            url={`${origin}/analysis`}
            accent="#FF9F0A"
          />
        </div>
      </div>

      {/* Full wall link */}
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#0A84FF] mb-2">TV 1 · Full Camera Wall (grid + AI)</p>
        <DisplayRow
          testid="tv-display-all"
          title="All Cameras — Surveillance Wall"
          sub="Every camera at once in one grid"
          url={`${origin}/tv`}
          accent="#0A84FF"
        />
      </div>

      {/* Auto-rotating link */}
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#32D74B] mb-2">TV 2 · Auto-Rotating Screen</p>
        <DisplayRow
          testid="tv-display-cycle"
          title="Rotating View — one camera at a time"
          sub="Holds each camera ~20s, skips quiet ones, and LOCKS on any camera with high activity until it calms down"
          url={`${origin}/cam`}
          accent="#32D74B"
        />
      </div>

      {/* Per-camera links */}
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#BF5AF2] mb-2">Single-Camera Displays ({cams.length})</p>
        {cams.length === 0 ? (
          <div className="border border-white/10 p-6 text-center">
            <p className="font-mono-data text-xs text-[#64748B]">No online cameras yet. Connect a camera to generate a TV link.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {cams.map((c) => (
              <DisplayRow
                key={c.id}
                testid={`tv-display-${c.id}`}
                title={c.name}
                sub={`${c.zone || "Camera"} · dedicated TV view`}
                url={`${origin}/tv?cam=${encodeURIComponent(c.id)}`}
                accent="#BF5AF2"
              />
            ))}
          </div>
        )}
      </div>

      <p className="flex items-center gap-1.5 font-mono-data text-[10px] tracking-[0.2em] text-[#3f3f46] uppercase pt-2">
        <ShieldCheck size={12} weight="fill" /> Powered by A V Tech
      </p>
    </div>
  );
}
